import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import { DateTimePicker } from "@hl/base-components/lib/DateTimePicker";
import { InfoCircleFill } from "@hl/base-components/lib/assets/icons.generated/index";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  Checked,
  NotChecked,
} from "@hl/shared-features/lib/assets/icons.generated";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import CryptoCurrencyInput from "@hl/shared-features/lib/features/input/CryptoCurrencyInput";
import {
  ActionIcon,
  Anchor,
  Card,
  Divider,
  Flex,
  Grid,
  Group,
  MantineTheme,
  NumberInput,
  NumberInputHandlers,
  OptionalPortal,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { ethers } from "ethers";

import {
  _CollectionType,
  DraftProgress,
  TokenDistributionType,
  PriceType,
  Contract2,
} from "apollo/graphql.generated";
import { CurrencyInput } from "components/sale/CurrencyInput";
import { SponsoredMintsInput } from "components/sale/SponsoredMintsInput";
import { ALCHEMY_API_KEY_ENS, SUPPORT_URL } from "config";
import { GateSelectOnCreate } from "~/components/GateSelect";
import DutchAuctionsCurrencyInput from "~/currency/DutchAuctionsCurrencyInput";
import DutchAuctionsPriceDropIntervalInput from "~/currency/DutchAuctionsPriceDropIntervalInput";
import FormContentWithPreview from "~/features/collections/forms/CreateCollectionMultistepForm/FormContentWithPreview";
import SaveDraftButton from "~/features/collections/forms/CreateCollectionMultistepForm/SaveDraftButton";
import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "~/features/collections/forms/CreateCollectionMultistepForm/context";
import { getPreviewImageUrl } from "~/features/collections/forms/CreateCollectionMultistepForm/previewUtil";
import useCurrencyDisplay from "~/features/collections/forms/CreateCollectionMultistepForm/useCurrencyDisplay";
import useSaveDraft from "~/features/collections/forms/CreateCollectionMultistepForm/useSaveDraft";
import useSponsoredMints from "~/features/collections/forms/CreateCollectionMultistepForm/useSponsoredMints";
import { useGatesQuery } from "~/features/gates/queries/gates.graphql.generated";
import PaymentRecipientInfoModal from "~/features/tokens/components/AdvancedFormModals/PaymentRecipientInfoModal";
import RoyaltyRecipientInfoModal from "~/features/tokens/components/AdvancedFormModals/RoyaltyRecipientInfoModal";

import FormTitle from "./FormTitle";
import NextStepButton from "./NextStepButton";
import SeriesMintStylePicker from "./SeriesMintStylePicker";

export const REASONABLE_SALE_PRICE_LIMIT = 50000000;

const CollectionPriceForm = ({
  contracts,
  asQuickDropFlow = false,
}: {
  contracts: Contract2[];
  asQuickDropFlow?: boolean;
}) => {
  const { data: gatesData } = useGatesQuery();
  const [loadingForm, setLoadingForm] = useState<boolean>(false);
  const [paymentRecipientModalOpen, setPaymentRecipientModalOpen] =
    useState<boolean>(false);
  const [royaltyRecipientModalOpen, setRoyaltyRecipientModalOpen] =
    useState<boolean>(false);
  const limitNumberOfMintsHandlers = useRef<NumberInputHandlers>();
  const enableDutchAuctionsCreation = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_DUTCH_AUCTIONS_CREATION
  );
  const enableDirectMinting = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_DIRECT_MINTING
  );
  const createCollectionsWithoutMintVectors = useFeatureFlags(
    FEATURE_FLAGS.CREATE_COLLECTIONS_WITHOUT_MINT_VECTORS
  );

  const { dispatch } = useCreateCollectionState();
  const form = useCreateCollectionFormContext();
  const formValues = form.values;

  const { saveDraft, loading } = useSaveDraft({
    furthestStep: DraftProgress.ADDED_DISTRIBUTION,
  });

  const { displayCurrency } = useCurrencyDisplay();
  const { disabledSponsoredMints } = useSponsoredMints(contracts);

  const onNextStep = () => {
    dispatch({
      type: "NEXT_STEP",
      payload: {
        form,
      },
    });
  };

  const onToggleAuction = () => {
    form.setFieldValue("distribution", {
      ...formValues.distribution,
      type: TokenDistributionType.Auction,
      priceType: PriceType.Fixed,
      priceDropInterval: undefined,
      scheduledPrices: undefined,
      isDirectMint: false,
      price: undefined,
      isFreePrice: false,
      currency: ethers.constants.AddressZero,
    });
  };
  const onToggleMint = () => {
    form.setFieldValue("distribution", {
      ...formValues.distribution,
      type: TokenDistributionType.Mint,
      priceType: PriceType.Fixed,
      priceDropInterval: undefined,
      scheduledPrices: undefined,
      price: undefined,
      isFreePrice: false,
    });
  };

  const resetFixedPriceProps = {
    price: undefined,
  };
  const resetDutchAuctionProps = {
    priceDropInterval: undefined,
    scheduledPrices: undefined,
  };
  const resetRankedAuctionProps = {
    minBid: undefined,
    rebateWinningBids: undefined,
  };

  const onTogglePriceFixed = () => {
    form.setFieldValue("distribution", {
      ...formValues.distribution,
      priceType: PriceType.Fixed,
      price: undefined,
      isFreePrice: false,
      ...resetDutchAuctionProps,
      ...resetRankedAuctionProps,
    });
  };

  const onToggleDutchAuction = () => {
    form.setFieldValue("distribution", {
      ...formValues.distribution,
      type: TokenDistributionType.Mint,
      priceType: PriceType.DutchAuction,
      isFreePrice: false,
      currency: ethers.constants.AddressZero,
      ...resetFixedPriceProps,
      ...resetRankedAuctionProps,
    });
    form.clearFieldError("distribution.saleDateTo");
  };
  const onToggleRankedAuction = () => {
    form.setFieldValue("distribution", {
      ...formValues.distribution,
      type: TokenDistributionType.Mint,
      priceType: PriceType.RankedAuction,

      ...resetFixedPriceProps,
      ...resetDutchAuctionProps,
    });
    form.setFieldValue("gateId", null);
    form.setFieldValue("metadata", {
      ...form.values.metadata,
      reveal: true,
    });
    form.clearFieldError("distribution.saleDateTo");
  };

  const onToggleFreePrice = () => {
    form.setFieldValue("distribution", {
      ...formValues.distribution,
      type: TokenDistributionType.Mint,
      priceType: PriceType.Fixed,
      priceDropInterval: undefined,
      scheduledPrices: undefined,
      price: 0,
      isFreePrice: true,
    });
  };

  const enableCreatorRewards = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_CREATOR_REWARDS
  );

  const priceType = formValues.distribution.priceType;
  const isAuction =
    formValues.distribution.type === TokenDistributionType.Auction;
  const isPriceFixed = priceType === PriceType.Fixed;
  const isDutchAuction = priceType === PriceType.DutchAuction;
  const isRankedAuction = priceType === PriceType.RankedAuction;
  const isFreePrice =
    priceType === PriceType.Fixed &&
    formValues.distribution.isFreePrice &&
    !!enableCreatorRewards;

  // allow direct minting on:
  // - any non-series mints
  // - any series random mints
  // - collector's choice dutch auction (but not fixed price)
  const allowDirectMinting =
    enableDirectMinting &&
    (formValues.collectionType !== _CollectionType.Series ||
      formValues.metadata.reveal ||
      (!formValues.metadata.reveal &&
        formValues.collectionType === _CollectionType.Series &&
        !isPriceFixed)) &&
    !isAuction;

  useEffect(() => {
    form.setFieldValue(
      "distribution.isDirectMint",
      allowDirectMinting && !formValues.gateId
    );
  }, [allowDirectMinting, formValues.gateId]);

  useEffect(() => {
    if (formValues.withoutMintVector) {
      form.setFieldValue(
        "moduleSettings.shouldLimitNumberOfMintPerWallet",
        false
      );

      form.clearFieldError("distribution.currency");
      form.clearFieldError("distribution.price");
      form.clearFieldError("distribution.scheduledPrices");
      form.clearFieldError("distribution.priceDropInterval");
      form.clearFieldError("distribution.saleDateFrom");
      form.clearFieldError("distribution.saleDateTo");
    }
  }, [formValues.withoutMintVector]);

  const ensUrl = `https://eth-mainnet.alchemyapi.io/v2/${ALCHEMY_API_KEY_ENS}`;
  const provider = useMemo(
    () =>
      ALCHEMY_API_KEY_ENS
        ? new ethers.providers.JsonRpcProvider(ensUrl)
        : ethers.getDefaultProvider(),
    [ensUrl]
  );

  const onSetPaymentRecipientEOA = async (val: string) => {
    if (val.endsWith(".eth")) {
      setLoadingForm(true);
      try {
        const address = await provider.resolveName(val);
        if (address) {
          form.setFieldValue(
            "moduleSettings.customPaymentRecipient.eoa",
            address
          );
          form.setFieldValue(
            "moduleSettings.customPaymentRecipient.ensName",
            val
          );
        } else {
          form.setFieldError(
            "moduleSettings.customPaymentRecipient.address",
            "Invalid ENS"
          );
        }
      } catch (e) {
        form.setFieldError(
          "moduleSettings.customPaymentRecipient.address",
          "Error fetching ENS. Try later or use the actual wallet address."
        );
      } finally {
        setLoadingForm(false);
      }
    } else {
      form.setFieldValue("moduleSettings.customPaymentRecipient.eoa", val);
    }
  };

  const onSetRoyaltyEOA = async (val: string) => {
    if (val.endsWith(".eth")) {
      setLoadingForm(true);
      try {
        const address = await provider.resolveName(val);
        if (address) {
          form.setFieldValue("moduleSettings.royalty.payoutEoa", address);
          form.setFieldValue("moduleSettings.royalty.payoutEnsName", val);
        } else {
          form.setFieldError("moduleSettings.royalty.address", "Invalid ENS");
        }
      } catch (e) {
        form.setFieldError(
          "moduleSettings.royalty.address",
          "Error fetching ENS. Try later or use the actual wallet address."
        );
      } finally {
        setLoadingForm(false);
      }
    } else {
      form.setFieldValue("moduleSettings.royalty.payoutEoa", val);
    }
  };

  const { previewMediaUrl, previewMediaType } = getPreviewImageUrl(form);

  const { collectionType } = formValues;
  const rankedAuctionNotSupported =
    collectionType == _CollectionType.OpenEdition ||
    (collectionType === _CollectionType.GenerativeSeries &&
      !formValues.collection.size);

  const enableRankedAuctions =
    useFeatureFlags(FEATURE_FLAGS.ENABLE_RANKED_AUCTIONS) &&
    !rankedAuctionNotSupported;

  const cardTheme = useCallback(
    (theme: MantineTheme) => ({
      ...(form.values.withoutMintVector && {
        opacity: 0.6,
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[6]
            : theme.colors.gray[1],
        color: theme.colors.dark[2],
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[4]
        }`,
      }),
    }),
    [form.values.withoutMintVector]
  );

  const auctionsHelpLink = useMemo(() => {
    if (isDutchAuction) return SUPPORT_URL.DUTCH_AUCTIONS;
    if (isRankedAuction) return SUPPORT_URL.RANKED_AUCTIONS;
    if (isAuction) return SUPPORT_URL.ENGLISH_AUCTIONS;
  }, [isDutchAuction, isRankedAuction, isAuction]);

  return (
    <div>
      {!asQuickDropFlow && (
        <FormTitle
          title="Mint details"
          buttons={[
            <SaveDraftButton
              key="draft"
              loading={loadingForm || loading}
              onClick={() => {
                saveDraft();
              }}
            />,
            <NextStepButton
              key="next"
              loading={loadingForm || loading}
              onClick={onNextStep}
            />,
          ]}
        />
      )}
      <FormContentWithPreview
        asQuickDropFlow={asQuickDropFlow}
        collectionType={
          formValues.collectionType ?? _CollectionType.LimitedEdition
        }
        mediaUrl={previewMediaUrl}
        mediaType={previewMediaType}
        showDetails
        name={formValues.collection.name}
        size={formValues.collection.size}
        showPrice={!!form.values.distribution.price}
        price={form.values.distribution.price}
        currency={displayCurrency}
      >
        <Stack spacing={40}>
          {formValues.collectionType == _CollectionType.OneOfOne ? (
            <Stack spacing={8}>
              <Text component="label" size="sm">
                Sale type
              </Text>
              <Flex
                justify="center"
                align="flex-start"
                direction={
                  enableDutchAuctionsCreation || enableCreatorRewards
                    ? "column"
                    : "row"
                }
                gap={12}
              >
                <SelectBox
                  checked={!isAuction && isPriceFixed && !isFreePrice}
                  label="Mint"
                  description="Sell this token for a fixed price that you set"
                  onClick={onToggleMint}
                />
                <SelectBox
                  checked={isAuction}
                  label="English auction"
                  description="Let people bid on this token in an auction with a
                        reserve price"
                  onClick={onToggleAuction}
                />
                {enableDutchAuctionsCreation && (
                  <SelectBox
                    checked={isDutchAuction}
                    label="Dutch auction"
                    description="Create a schedule of decreasing prices"
                    onClick={onToggleDutchAuction}
                  />
                )}
                {enableCreatorRewards && (
                  <SelectBox
                    checked={!isAuction && isFreePrice}
                    label="Free mint"
                    description="Let collector mint for free and earn 50% of the mint"
                    onClick={onToggleFreePrice}
                  />
                )}
              </Flex>
            </Stack>
          ) : (
            (enableDutchAuctionsCreation || enableRankedAuctions) && (
              <Stack spacing={8}>
                <Text component="label" size="sm">
                  Sale type
                </Text>
                <Flex
                  justify="center"
                  align="flex-start"
                  direction={
                    enableDutchAuctionsCreation || enableCreatorRewards
                      ? "column"
                      : "row"
                  }
                  gap={12}
                >
                  {enableCreatorRewards && (
                    <SelectBox
                      checked={isFreePrice}
                      label="Free"
                      description="Let collectors mint for free and earn 50% of Highlight’s mint fees"
                      onClick={onToggleFreePrice}
                      disabled={form.values.withoutMintVector}
                    />
                  )}
                  <SelectBox
                    checked={isPriceFixed && !isFreePrice}
                    label="Fixed price"
                    description="Sell this token for a fixed price that you set"
                    onClick={onTogglePriceFixed}
                    disabled={form.values.withoutMintVector}
                  />
                  {enableDutchAuctionsCreation && (
                    <SelectBox
                      checked={isDutchAuction}
                      label="Dutch auction"
                      description="Create a schedule of decreasing prices"
                      onClick={onToggleDutchAuction}
                      disabled={form.values.withoutMintVector}
                    />
                  )}
                  {enableRankedAuctions && (
                    <SelectBox
                      checked={isRankedAuction}
                      label="Ranked auction"
                      description="Distribute the tokens to all top bidders"
                      onClick={onToggleRankedAuction}
                      disabled={form.values.withoutMintVector}
                    />
                  )}
                </Flex>
              </Stack>
            )
          )}
          {(isDutchAuction || isRankedAuction || isAuction) && (
            <Text size="sm" mt="-md">
              Highlight charges a 5% fee for{" "}
              {isDutchAuction
                ? "public dutch "
                : isRankedAuction
                ? "ranked "
                : ""}
              auctions. Learn more{" "}
              <Anchor
                target="_blank"
                href={auctionsHelpLink}
                fw={WEIGHT_BOLD}
                size="sm"
              >
                here
              </Anchor>
            </Text>
          )}
          <>
            {isPriceFixed && !isAuction && (
              <CurrencyInput
                label={isFreePrice ? "Mint fee currency" : "Currency"}
                description={
                  isFreePrice
                    ? "The currency collectors will pay and you will receive in mint rewards"
                    : "Select the currency you want collectors to pay"
                }
                value={formValues.distribution.currency}
                onChange={(address) =>
                  form.setFieldValue("distribution.currency", address)
                }
                chainId={+formValues.collection.chainId}
                disabled={form.values.withoutMintVector}
                error={form.errors.currency}
              />
            )}
            {isPriceFixed && !isFreePrice && (
              <CryptoCurrencyInput
                label={isAuction ? "Reserve price" : "Initial sale price"}
                description={
                  isAuction
                    ? "The minimum amount you will accept as a bid. If this amount is not reached by the end of the auction, the NFT will not be sold."
                    : "Once your collection is deployed, you’ll be able to enable additional sales mechanics for presales, discounts, and more. For now, please choose an initial sale price which can be edited later."
                }
                placeholder="0.1"
                missingChainIdPlaceholder="Select a blockchain before entering price"
                chainId={formValues.collection.chainId}
                max={REASONABLE_SALE_PRICE_LIMIT}
                disabled={form.values.withoutMintVector}
                currencySymbol={displayCurrency}
                {...form.getInputProps("distribution.price")}
              />
            )}
            {isDutchAuction && (
              <>
                <DutchAuctionsCurrencyInput
                  value={form.values.distribution?.scheduledPrices}
                  chainId={formValues.collection.chainId}
                  error={
                    form.getInputProps("distribution.scheduledPrices").error
                  }
                  onChange={
                    form.getInputProps("distribution.scheduledPrices").onChange
                  }
                  forceDisable={form.values.withoutMintVector}
                />
                <DutchAuctionsPriceDropIntervalInput
                  value={form.values.distribution?.priceDropInterval}
                  error={
                    form.getInputProps("distribution.priceDropInterval").error
                  }
                  onChange={
                    form.getInputProps("distribution.priceDropInterval")
                      .onChange
                  }
                  disabled={form.values.withoutMintVector}
                />
              </>
            )}
            {isRankedAuction && (
              <>
                <CryptoCurrencyInput
                  label={"Minimum bid"}
                  description={
                    "Set the minimum amount a collector must pay to bid."
                  }
                  placeholder="0.1"
                  missingChainIdPlaceholder="Select a blockchain before entering price"
                  chainId={formValues.collection.chainId}
                  max={REASONABLE_SALE_PRICE_LIMIT}
                  disabled={form.values.withoutMintVector}
                  {...form.getInputProps("distribution.minBid")}
                />
                <Card p="xs" mt={8} sx={cardTheme}>
                  <Stack spacing={0}>
                    <Group position="apart" align="center">
                      <Text
                        size="sm"
                        sx={(theme) => ({
                          ...(form.values.withoutMintVector && {
                            color: theme.colors.dark[2],
                          }),
                        })}
                      >
                        Enable rebates for all winning bids
                      </Text>
                      <Switch
                        mt={4}
                        disabled={form.values.withoutMintVector}
                        {...form.getInputProps(
                          "distribution.rebateWinningBids",
                          {
                            type: "checkbox",
                          }
                        )}
                      />
                    </Group>
                    <Text size="xs" color={TEXT_COLOR.SECONDARY}>
                      When enabled, all winning bidders will pay the lowest
                      winning bid amount and reclaim any extra amount as a
                      rebate.
                    </Text>
                  </Stack>
                </Card>
              </>
            )}

            <DateTimePicker
              label={`${isAuction ? "Auction" : "Mint"} start date`}
              clearable={false}
              {...form.getInputProps("distribution.saleDateFrom")}
              minDate={new Date()}
              disabled={form.values.withoutMintVector}
            />
            <DateTimePicker
              clearable={!isAuction}
              label={`${isAuction ? "Auction" : "Mint"} end date`}
              placeholder="None"
              {...form.getInputProps("distribution.saleDateTo")}
              minDate={new Date()}
              disabled={form.values.withoutMintVector}
            />
            {formValues.collectionType === _CollectionType.Series && (
              <SeriesMintStylePicker
                reveal={formValues.metadata.reveal}
                hideCollectorChoice={isRankedAuction}
                setReveal={(reveal: boolean) => {
                  form.setValues({
                    metadata: { ...formValues.metadata, reveal },
                  });
                }}
                previewImages={formValues.metadata.previewImages}
                setPreviewImages={(
                  previewImages: { url: string; type: string; name: string }[]
                ) => {
                  form.setValues({
                    metadata: { ...formValues.metadata, previewImages },
                  });
                }}
                showAllMetadata={formValues.metadata.showAllMetadata}
                setShowAllMetadata={(showAllMetadata: boolean) => {
                  form.setValues({
                    metadata: { ...formValues.metadata, showAllMetadata },
                  });
                }}
                mediaError={
                  (form.errors["metadata.previewImageUrls"] as string) ?? ""
                }
              />
            )}
          </>

          <OptionalPortal
            withinPortal={asQuickDropFlow}
            target={"#advanced-options-portal-target"}
            className="optional-portal"
          >
            <TextInput
              placeholder="e.g. vitalik.eth, 0x0000000000000000000000000000000000000000"
              onInput={(e) => onSetPaymentRecipientEOA(e.currentTarget.value)}
              spellCheck={false}
              label={
                <>
                  <Group spacing={4}>
                    {isAuction ? "Auction" : "Primary sales"} payout address
                    <InfoCircleFill
                      width={17}
                      height={17}
                      stroke={"#000000"}
                      cursor={"pointer"}
                      onClick={() => setPaymentRecipientModalOpen(true)}
                    />
                  </Group>
                </>
              }
              disabled={form.values.withoutMintVector}
              {...form.getInputProps(
                "moduleSettings.customPaymentRecipient.address"
              )}
            />
            {/* don't use number/text-input label so on smaller screens, the input is not offset by the wrapped labels */}
            {/* i.e. "royalty amount" label will wrap - take 2 lines and there's a weird offset */}
            {!formValues.moduleSettings.nonTransferable && (
              <Group spacing="sm">
                <NumberInput
                  label="Royalty amount"
                  data-testid="royalty-amount-input"
                  min={0}
                  max={99}
                  precision={2}
                  placeholder={0}
                  removeTrailingZeros={true}
                  w={110}
                  rightSection="%"
                  rightSectionWidth={50}
                  styles={{
                    rightSection: { fontSize: 14 },
                    input: { paddingRight: 32 },
                  }}
                  {...form.getInputProps(
                    "moduleSettings.royalty.royaltyAmount"
                  )}
                />
                <TextInput
                  label={
                    <Group spacing={4}>
                      Royalty payout address/ENS
                      <InfoCircleFill
                        width={17}
                        height={17}
                        stroke={"#000000"}
                        cursor="pointer"
                        onClick={() => setRoyaltyRecipientModalOpen(true)}
                      />
                    </Group>
                  }
                  sx={{ flexGrow: 1 }}
                  data-testid="royalty-recipient-address-input"
                  placeholder="e.g. vitalik.eth, 0x0000000000000000000000000000000000000000"
                  onInput={(e) => onSetRoyaltyEOA(e.currentTarget.value)}
                  spellCheck={false}
                  {...form.getInputProps("moduleSettings.royalty.address")}
                />
              </Group>
            )}
            <>
              <Stack spacing={0}>
                <Text size="sm" fw={500} component="label">
                  Minting options
                </Text>
                {formValues.distribution.type ===
                  TokenDistributionType.Mint && (
                  <Card p="xs" mt={8} sx={cardTheme}>
                    <Group position="apart" align="center" noWrap>
                      <Text
                        size="sm"
                        sx={(theme) => ({
                          ...(form.values.withoutMintVector && {
                            color: theme.colors.dark[2],
                          }),
                        })}
                      >
                        Limit number of mints per wallet
                      </Text>
                      <Switch
                        mt={4}
                        disabled={form.values.withoutMintVector}
                        {...form.getInputProps(
                          "moduleSettings.shouldLimitNumberOfMintPerWallet",
                          {
                            type: "checkbox",
                          }
                        )}
                      />
                    </Group>
                    {formValues.moduleSettings
                      .shouldLimitNumberOfMintPerWallet && (
                      <>
                        <Divider my="xs" />
                        <Group position="apart" align="stretch">
                          <Text size="sm">
                            Maximum mints allowed per wallet
                          </Text>
                          <Group spacing={0}>
                            <NumberInput
                              min={1}
                              max={10000}
                              {...form.getInputProps(
                                "moduleSettings.limitPerWallet"
                              )}
                              handlersRef={limitNumberOfMintsHandlers}
                              hideControls
                              hidden
                            />
                            <Text size="sm" mr="8px">
                              {formValues.moduleSettings.limitPerWallet}
                            </Text>
                            <ActionIcon
                              size={20}
                              color={SECONDARY_COLOR}
                              onClick={() =>
                                limitNumberOfMintsHandlers?.current?.decrement()
                              }
                              mr="1px"
                            >
                              –
                            </ActionIcon>
                            <ActionIcon
                              size={20}
                              color={SECONDARY_COLOR}
                              onClick={() =>
                                limitNumberOfMintsHandlers?.current?.increment()
                              }
                            >
                              +
                            </ActionIcon>
                          </Group>
                        </Group>
                      </>
                    )}
                  </Card>
                )}
              </Stack>

              <SponsoredMintsInput
                inputProps={form.getInputProps(
                  "distribution.sponsoredMintsEnabled",
                  {
                    type: "checkbox",
                  }
                )}
                disabled={disabledSponsoredMints}
                chainSupportsERC20s={[
                  "8453",
                  "7777777",
                  "84532",
                  "999",
                ].includes(formValues.collection.chainId)}
                isSeries={formValues.collectionType == _CollectionType.Series}
              />

              {!isRankedAuction && (
                <GateSelectOnCreate
                  description={`Select a gate to determine who can ${
                    isAuction ? "bid on" : "mint"
                  } this collection`}
                  gates={gatesData?.gates ?? []}
                  value={form.values.gateId ?? ""}
                  onChange={(newGateId: string) =>
                    form.setFieldValue("gateId", newGateId)
                  }
                  isGatingMint={!isAuction}
                  isGatingAuction={isAuction}
                  isDirectMint={false}
                  disabled={form.values.withoutMintVector}
                />
              )}
            </>
            {createCollectionsWithoutMintVectors &&
              [
                _CollectionType.Series,
                _CollectionType.GenerativeSeries,
              ].includes(formValues.collectionType) && (
                <Stack spacing="xs">
                  <Text size="sm" fw={WEIGHT_BOLD} component="label">
                    Advanced options
                  </Text>
                  <Group
                    p="xs"
                    position="apart"
                    align="center"
                    sx={{
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      borderRadius: "6px",
                    }}
                  >
                    <Text size="sm">Deploy without an initial sale</Text>
                    <Switch
                      sx={{
                        input: {
                          height: "100%",
                        },
                      }}
                      color="dark"
                      {...form.getInputProps("withoutMintVector", {
                        type: "checkbox",
                      })}
                    />
                  </Group>
                  <Text size="xs" color={TEXT_COLOR.SECONDARY}>
                    Sales can be configured after you deploy
                  </Text>
                </Stack>
              )}
          </OptionalPortal>
          {!asQuickDropFlow && (
            <NextStepButton
              size="xl"
              loading={loadingForm || loading}
              onClick={onNextStep}
            />
          )}
        </Stack>
      </FormContentWithPreview>
      <PaymentRecipientInfoModal
        isOpen={paymentRecipientModalOpen}
        onClose={() => setPaymentRecipientModalOpen(false)}
      />
      <RoyaltyRecipientInfoModal
        isOpen={royaltyRecipientModalOpen}
        onClose={() => setRoyaltyRecipientModalOpen(false)}
      />
    </div>
  );
};

const SelectBox = ({
  label,
  description,
  checked,
  onClick,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <BoxContainer
    w="100%"
    h="100%"
    sx={(theme) => ({
      cursor: "pointer",
      border: checked ? `2px solid ${theme.colors.primaryText[0]}` : undefined,
      ...(disabled && {
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[6]
            : theme.colors.gray[1],
        color: theme.colors.dark[2],
        border: `1px solid ${theme.colors.divider[0]}`,
      }),
    })}
    onClick={() => {
      if (!disabled) {
        onClick();
      }
    }}
  >
    <Grid gutter={16} align="flex-start" justify="space-between">
      <Grid.Col span={8}>
        <Text size="sm">{label}</Text>
        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          {description}
        </Text>
      </Grid.Col>
      <Grid.Col span={2}>
        <Group align="flex-end" position="right">
          {checked ? <Checked width={20} /> : <NotChecked width={20} />}
        </Group>
      </Grid.Col>
    </Grid>
  </BoxContainer>
);
export default memo(CollectionPriceForm);
